import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

interface SidebarItemProps {
  to: string;
  children: React.ReactNode;
  onClick?: () => void;
  isSubitem?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ to, children, onClick, isSubitem = false }) => {
  const location = useLocation();
  
  // Check if this item is active, handling both regular routes and anchor links
  const isActive = () => {
    if (to.includes('#')) {
      // For anchor links, check if current location matches the base path and hash
      const [basePath, hash] = to.split('#');
      return location.pathname === basePath && location.hash === `#${hash}`;
    } else {
      // For regular links, just check the pathname
      return location.pathname === to;
    }
  };
  
  const handleClick = (e: React.MouseEvent) => {
    if (onClick) onClick();
    
    // Handle anchor links scrolling
    if (to.includes('#')) {
      const sectionId = to.split('#')[1];
      const element = document.getElementById(sectionId);
      
      if (element) {
        e.preventDefault();
        element.scrollIntoView({ behavior: 'smooth' });
        // Update URL without triggering navigation
        window.history.pushState({}, '', to);
      }
    }
  };

  const activeClass = isActive() ? 'bg-osrs-gold text-osrs-black font-bold' : 'text-osrs-yellow hover:bg-osrs-dark-brown hover:text-osrs-gold';
  const classNames = `block py-2 px-4 rounded ${isSubitem ? 'ml-4 text-sm' : ''} ${activeClass}`;

  return (
    <NavLink
      to={to}
      onClick={handleClick}
      className={classNames}
    >
      {children}
    </NavLink>
  );
};

interface SidebarSectionProps {
  title: string;
  children: React.ReactNode;
}

const SidebarSection: React.FC<SidebarSectionProps> = ({ title, children }) => {
  return (
    <div className="mb-6">
      <h3 className="text-osrs-gold font-bold uppercase mb-3 px-4">{title}</h3>
      <div className="space-y-1">
        {children}
      </div>
    </div>
  );
};

interface SidebarProps {
  className?: string;
  pageType: 'foundations' | 'components' | 'patterns' | 'resources';
}

interface LinkItem {
  to: string;
  label: string;
  sections?: Array<{
    id: string;
    label: string;
  }>;
}

const Sidebar: React.FC<SidebarProps> = ({ className = '', pageType }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const location = useLocation();
  
  const linksToShow = useCallback((): LinkItem[] => {
    const foundationsLinks: LinkItem[] = [
      { 
        to: '/foundations/colors', 
        label: 'Colors',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'primary-colors', label: 'Primary Colors' },
          { id: 'ui-colors', label: 'UI Colors' },
          { id: 'status-colors', label: 'Status Colors' },
          { id: 'text-colors', label: 'Text Colors' },
          { id: 'implementation', label: 'Implementation' },
          { id: 'usage', label: 'Usage Guidelines' }
        ]
      },
      { 
        to: '/foundations/typography', 
        label: 'Typography',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'fonts', label: 'Font Families' },
          { id: 'scale', label: 'Type Scale' },
          { id: 'styles', label: 'Text Styles' },
          { id: 'implementation', label: 'Implementation' },
          { id: 'usage', label: 'Usage Guidelines' }
        ]
      },
      { 
        to: '/foundations/spacing', 
        label: 'Spacing',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'scale', label: 'Spacing Scale' },
          { id: 'usage', label: 'Usage Guidelines' }
        ]
      },
      { 
        to: '/foundations/borders', 
        label: 'Borders',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'styles', label: 'Border Styles' },
          { id: 'implementation', label: 'Implementation' },
          { id: 'usage', label: 'Usage Guidelines' }
        ]
      },
      { 
        to: '/foundations/shadows', 
        label: 'Shadows',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'styles', label: 'Shadow Styles' },
          { id: 'implementation', label: 'Implementation' },
          { id: 'usage', label: 'Usage Guidelines' }
        ]
      },
    ];
    
    const componentLinks: LinkItem[] = [
      { 
        to: '/components/button', 
        label: 'Button',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'examples', label: 'Examples' },
          { id: 'variants', label: 'Variants' },
          { id: 'sizes', label: 'Sizes' },
          { id: 'full-width', label: 'Full Width' },
          { id: 'disabled', label: 'Disabled State' },
          { id: 'on-click', label: 'Event Handling' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' },
          { id: 'api-reference', label: 'API Reference' }
        ]
      },
      { 
        to: '/components/panel', 
        label: 'Panel',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'examples', label: 'Examples' },
          { id: 'variants', label: 'Variants' },
          { id: 'with-title', label: 'With Title' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' },
          { id: 'api-reference', label: 'API Reference' }
        ]
      },
      { 
        to: '/components/input', 
        label: 'Input Fields',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'text-input', label: 'Text Input' },
          { id: 'number-input', label: 'Number Input' },
          { id: 'states', label: 'States' },
          { id: 'validation', label: 'Validation' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' },
          { id: 'api-reference', label: 'API Reference' }
        ]
      },
      { 
        to: '/components/checkbox', 
        label: 'Checkbox',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'examples', label: 'Examples' },
          { id: 'states', label: 'States' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' },
          { id: 'api-reference', label: 'API Reference' }
        ]
      },
      { 
        to: '/components/dialog', 
        label: 'Dialog',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'examples', label: 'Examples' },
          { id: 'variants', label: 'Variants' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' },
          { id: 'api-reference', label: 'API Reference' }
        ]
      },
      { 
        to: '/components/tooltip', 
        label: 'Tooltip',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'examples', label: 'Examples' },
          { id: 'positioning', label: 'Positioning' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' },
          { id: 'api-reference', label: 'API Reference' }
        ]
      },
      { 
        to: '/components/scrollbar', 
        label: 'Scrollbar',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'examples', label: 'Examples' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' },
          { id: 'api-reference', label: 'API Reference' }
        ]
      },
      { 
        to: '/components/progress-bar', 
        label: 'Progress Bar',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'examples', label: 'Examples' },
          { id: 'variants', label: 'Variants' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' },
          { id: 'api-reference', label: 'API Reference' }
        ]
      },
      { 
        to: '/components/inventory-slot', 
        label: 'Inventory Slot',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'examples', label: 'Examples' },
          { id: 'states', label: 'States' },
          { id: 'with-item', label: 'With Item' },
          { id: 'with-quantity', label: 'With Quantity' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' },
          { id: 'api-reference', label: 'API Reference' }
        ]
      },
    ];
    
    const patternLinks: LinkItem[] = [
      {
        to: '/patterns/navigation',
        label: 'Navigation',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'menu-bar', label: 'Menu Bar' },
          { id: 'sidebar', label: 'Sidebar' },
          { id: 'tabs', label: 'Tabs' },
          { id: 'breadcrumbs', label: 'Breadcrumbs' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' }
        ]
      },
      {
        to: '/patterns/forms',
        label: 'Forms',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'layout', label: 'Layout' },
          { id: 'validation', label: 'Validation' },
          { id: 'error-handling', label: 'Error Handling' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' }
        ]
      },
      {
        to: '/patterns/dialogs',
        label: 'Dialogs',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'alerts', label: 'Alerts' },
          { id: 'confirmations', label: 'Confirmations' },
          { id: 'modal-dialogs', label: 'Modal Dialogs' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' }
        ]
      },
      {
        to: '/patterns/inventory',
        label: 'Inventory Management',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'grid-layout', label: 'Grid Layout' },
          { id: 'drag-and-drop', label: 'Drag and Drop' },
          { id: 'stacking', label: 'Stacking Items' },
          { id: 'organization', label: 'Organization' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' }
        ]
      },
      {
        to: '/patterns/chat',
        label: 'Chat Interface',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'message-layout', label: 'Message Layout' },
          { id: 'input-area', label: 'Input Area' },
          { id: 'channels', label: 'Channels' },
          { id: 'formatting', label: 'Text Formatting' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' }
        ]
      },
    ];
    
    const resourcesLinks: LinkItem[] = [
      {
        to: '/resources/downloads',
        label: 'Downloads',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'design-files', label: 'Design Files' },
          { id: 'code-assets', label: 'Code Assets' },
          { id: 'textures', label: 'Textures' }
        ]
      },
      {
        to: '/resources/figma',
        label: 'Figma Library',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'getting-started', label: 'Getting Started' },
          { id: 'components', label: 'Components' },
          { id: 'styles', label: 'Styles' },
          { id: 'templates', label: 'Templates' }
        ]
      },
      {
        to: '/resources/icons',
        label: 'Icons',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'interface-icons', label: 'Interface Icons' },
          { id: 'item-icons', label: 'Item Icons' },
          { id: 'skill-icons', label: 'Skill Icons' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' }
        ]
      },
      {
        to: '/resources/fonts',
        label: 'Fonts',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'downloads', label: 'Downloads' },
          { id: 'installation', label: 'Installation' },
          { id: 'usage-guidelines', label: 'Usage Guidelines' }
        ]
      },
      {
        to: '/resources/implementation',
        label: 'Implementation Guide',
        sections: [
          { id: 'overview', label: 'Overview' },
          { id: 'getting-started', label: 'Getting Started' },
          { id: 'react', label: 'React Implementation' },
          { id: 'tailwind', label: 'Tailwind CSS Setup' },
          { id: 'styling', label: 'Styling Conventions' },
          { id: 'best-practices', label: 'Best Practices' }
        ]
      },
    ];
    
    switch (pageType) {
      case 'foundations':
        return foundationsLinks;
      case 'components':
        return componentLinks;
      case 'patterns':
        return patternLinks;
      case 'resources':
        return resourcesLinks;
      default:
        return [];
    }
  }, [pageType]);

  // Update expanded item based on current route
  useEffect(() => {
    // Get current links based on pageType
    const currentLinks = linksToShow();
    
    const currentPath = location.pathname;
    const currentItem = currentLinks.find(link => 
      currentPath === link.to || currentPath.startsWith(link.to + '/')
    );
    
    if (currentItem) {
      setExpandedItem(currentItem.to);
    } else {
      setExpandedItem(null);
    }
  }, [location.pathname, pageType, linksToShow]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleItemClick = (item: LinkItem) => {
    if (item.sections && item.sections.length > 0) {
      setExpandedItem(expandedItem === item.to ? null : item.to);
    }
    setIsMobileMenuOpen(false);
  };
  
  return (
    <>
      {/* Mobile Dropdown */}
      <div className="md:hidden w-full mb-6">
        <button 
          onClick={toggleMobileMenu}
          className="w-full bg-osrs-panel border-3 border-osrs-border rounded p-4 flex justify-between items-center"
          aria-expanded={isMobileMenuOpen}
        >
          <span className="text-osrs-gold font-bold uppercase">
            {pageType.charAt(0).toUpperCase() + pageType.slice(1)}
          </span>
          <svg 
            className={`w-5 h-5 text-osrs-gold transform ${isMobileMenuOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isMobileMenuOpen && (
          <div className="bg-osrs-panel border-3 border-t-0 border-osrs-border rounded-b p-4 mt-[-3px]">
            <div className="space-y-1">
              {linksToShow().map((link) => (
                <React.Fragment key={link.to}>
                  <SidebarItem to={link.to} onClick={() => handleItemClick(link)}>
                    {link.label}
                  </SidebarItem>
                  
                  {expandedItem === link.to && link.sections && (
                    <div className="mt-1 mb-2 space-y-1">
                      {link.sections.map(section => (
                        <SidebarItem 
                          key={`${link.to}#${section.id}`}
                          to={`${link.to}#${section.id}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                          isSubitem
                        >
                          {section.label}
                        </SidebarItem>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Desktop Sidebar */}
      <div className={`hidden md:block bg-osrs-panel border-3 border-osrs-border rounded p-4 w-full ${className}`}>
        <SidebarSection title={pageType.charAt(0).toUpperCase() + pageType.slice(1)}>
          {linksToShow().map((link) => (
            <React.Fragment key={link.to}>
              <SidebarItem to={link.to} onClick={() => handleItemClick(link)}>
                {link.label}
              </SidebarItem>
              
              {expandedItem === link.to && link.sections && (
                <div className="mt-1 mb-2 space-y-1">
                  {link.sections.map(section => (
                    <SidebarItem 
                      key={`${link.to}#${section.id}`}
                      to={`${link.to}#${section.id}`}
                      isSubitem
                    >
                      {section.label}
                    </SidebarItem>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </SidebarSection>
      </div>
    </>
  );
};

export default Sidebar; 